import { FundingAward } from '@/features/content-registration/models/FundingAward';
import {
  ContentRegistrationType,
  type ResourceFieldsImported,
} from '@/common/types';
import type { FormData } from '@content-registration/types';
import { normalizeDoi } from '@/utils/doi';

const nonBlank = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

/** A funding row the converter would emit: at least one assertion field is set. */
export const fundingRowHasData = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') return false;
  const funder = row as Record<string, unknown>;
  const award = new FundingAward(
    nonBlank(funder.awardNumber),
    nonBlank(funder['funder-id']),
    nonBlank(funder['funder-name']),
    nonBlank(funder.funderRorId)
  );
  return !award.isEmpty();
};

/** A relationship row the converter would emit: both type and related DOI are set. */
export const relationshipRowHasData = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') return false;
  const relationship = row as Record<string, unknown>;
  return (
    nonBlank(relationship.relationType) != null &&
    nonBlank(relationship.relatedDoi) != null
  );
};

/** References the converter would emit: at least one non-blank line. */
export const referencesHaveData = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  return value.split('\n').some((line) => line.trim() !== '');
};

/**
 * Records which journal-article resource parents had data in the loaded form.
 * Taken once at import. A missing article, or a field with no usable value,
 * is false.
 */
export const snapshotJournalArticleResourceFields = (formData: {
  article?: Record<string, unknown> | null;
}): ResourceFieldsImported => {
  const article = formData?.article ?? {};
  const funding = article.funding;
  const relationships = article.relationships;
  return {
    funding: Array.isArray(funding) && funding.some(fundingRowHasData),
    license: nonBlank(article.license) != null,
    simCheckUrl: nonBlank(article.simCheckUrl) != null,
    relationships:
      Array.isArray(relationships) &&
      relationships.some(relationshipRowHasData),
    references: referencesHaveData(article.references),
  };
};

/** A grant relationship row: both type and related DOI are set. */
const grantRelationshipHasData = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') return false;
  const relationship = row as Record<string, unknown>;
  return (
    nonBlank(relationship.type_of_relationship) != null &&
    nonBlank(relationship.related_doi) != null
  );
};

/** `{ relationships }` for a grant. */
export const snapshotGrantResourceFields = (formData: {
  grantMetadata?: { relationships?: unknown } | null;
}): ResourceFieldsImported => {
  const relationships = formData?.grantMetadata?.relationships;
  return {
    relationships:
      Array.isArray(relationships) &&
      relationships.some(grantRelationshipHasData),
  };
};

/** `{ references }` for a report or dissertation. */
export const snapshotReferences = (
  references: unknown
): ResourceFieldsImported => ({
  references: referencesHaveData(references),
});

/**
 * Conference papers record Similarity Check and references.
 * A flag is true when any paper had that field.
 */
export const snapshotConferenceResourceFields = (formData: {
  papers?: Array<{
    similarityCheckUrl?: unknown;
    references?: unknown;
  }> | null;
}): ResourceFieldsImported => {
  const papers = formData?.papers ?? [];
  return {
    simCheckUrl: papers.some(
      (paper) => nonBlank(paper?.similarityCheckUrl) != null
    ),
    references: papers.some((paper) => referencesHaveData(paper?.references)),
  };
};

/**
 * Books record Similarity Check and the book's own references.
 * `chapters` records each imported chapter that has a DOI.
 * A chapter with no DOI cannot be matched on redeposit, so it is left out.
 */
export const snapshotBookResourceFields = (formData: {
  bookMetadata?: {
    similarityCheckUrl?: unknown;
    references?: unknown;
  } | null;
  chapters?: Array<{ doi?: string; references?: unknown }> | null;
}): ResourceFieldsImported => {
  const meta = formData?.bookMetadata ?? {};
  return {
    simCheckUrl: nonBlank(meta.similarityCheckUrl) != null,
    references: referencesHaveData(meta.references),
    chapters: Object.fromEntries(
      (formData?.chapters ?? []).flatMap((chapter) =>
        chapter?.doi
          ? [
              [
                normalizeDoi(chapter.doi),
                { references: referencesHaveData(chapter.references) },
              ],
            ]
          : []
      )
    ),
  };
};

/** A chapter emits an empty citation list only when its imported references were cleared. */
export const chapterReferencesRemoved = (
  chapter: { doi?: string; references?: unknown },
  importedChapters: Readonly<Record<string, { references: boolean }>> | undefined
): boolean => {
  if (!chapter.doi || !importedChapters) return false;
  const imported = importedChapters[normalizeDoi(chapter.doi)];
  return imported?.references === true && !referencesHaveData(chapter.references);
};

/**
 * Which resource fields had data when this record was imported.
 * Spread onto `origin`. Books are snapshotted later, after chapters are
 * merged. A type with nothing to track returns an empty object.
 */
export function snapshotImportedResourceFields(
  recordType: ContentRegistrationType,
  formData: FormData
): { resourceFieldsImported?: ResourceFieldsImported } {
  switch (recordType) {
    case ContentRegistrationType.JournalArticle:
      return {
        resourceFieldsImported: snapshotJournalArticleResourceFields(formData),
      };
    case ContentRegistrationType.Report:
      return {
        resourceFieldsImported: snapshotReferences(
          formData.reportMetadata?.references
        ),
      };
    case ContentRegistrationType.Dissertation:
      return {
        resourceFieldsImported: snapshotReferences(
          formData.dissertationMetadata?.references
        ),
      };
    case ContentRegistrationType.ConferenceProceeding:
      return {
        resourceFieldsImported: snapshotConferenceResourceFields(formData),
      };
    case ContentRegistrationType.Grant:
      return {
        resourceFieldsImported: snapshotGrantResourceFields(formData),
      };
    default:
      return {};
  }
}
